import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, Search, FileDown, Home, Users, ClipboardList, Star, FileText, Gift, FileUser, GraduationCap, CalendarDays, FolderCheck, PenSquare, BookOpen, Target, ExternalLink, Layers, Library, UserCog, Loader2 } from "lucide-react";
import { API, authHeaders, errorText } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LeadDetail } from "./AdminLeadDetail";
import { ToolkitTable } from "./AdminToolkit";
import { ToolkitFiles } from "./AdminFiles";
import { InterestTable } from "./AdminInterest";
import { BrochureTable } from "./AdminBrochure";
import { ResumeLeadsTable } from "./AdminResume";
import { TrackingTab } from "./AdminTracking";
import { BlogTab } from "./AdminBlog";
import { ContactsProvider, CrmCells, PeopleTab } from "./AdminCrm";
import { Students, Sessions } from "./AdminLms";
import { Projects } from "./AdminLmsTab";
import { AdminHome } from "./AdminHome";
import { Batches } from "./AdminBatches";
import { Materials } from "./AdminMaterials";
import { AskProvider } from "@/components/admin/AskDialog";
import { MeContext, canDo } from "@/components/admin/Me";
import { Team, AcceptInvite } from "./AdminTeam";

// [group, area a sub-admin needs (see PERMISSIONS in server.py), items]
const NAV = [
  [null, null, [["home", "Home", Home]]],
  ["Leads", "leads", [["people", "People & timeline", Users], ["diagnostic", "Diagnostic leads", ClipboardList], ["interest", "Course interest", Star],
    ["brochure", "Curriculum downloads", FileText], ["toolkit", "Toolkit sign-ups", Gift], ["resume", "Resume Studio", FileUser]]],
  ["Learning portal", "learning", [["batches", "Batches & Zoom", Layers], ["students", "Students & access", GraduationCap], ["sessions", "Sessions & resources", CalendarDays],
    ["materials", "Study material", Library], ["projects", "Projects & reviews", FolderCheck]]],
  ["Content", "content", [["blog", "Blog", PenSquare], ["files", "Toolkit book", BookOpen]]],
  ["Settings", "owner", [["tracking", "Ad tracking", Target], ["team", "Team & access", UserCog]]],
];
const PANES = { home: null, people: PeopleTab, interest: InterestTable, brochure: BrochureTable, toolkit: ToolkitTable, resume: ResumeLeadsTable,
  batches: Batches, students: Students, sessions: Sessions, materials: Materials, projects: Projects, blog: BlogTab, files: ToolkitFiles, tracking: TrackingTab, team: Team };


const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("aor_admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(errorText(err));
    } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-[#0B1B33] px-5">
      <form data-testid="admin-login-card" onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-8">
        <Logo sub="ADMIN" testId="admin-logo" />
        <h1 className="font-display pt-2 text-[24px] font-bold text-[#0B1B33]">Lead dashboard</h1>
        <input data-testid="admin-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-3 outline-none focus:border-[#0E9384]" />
        <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-3 outline-none focus:border-[#0E9384]" />
        <button data-testid="admin-login-btn" disabled={busy} className="btn-teal w-full rounded-lg py-3 font-semibold disabled:opacity-60">{busy ? "Signing in..." : "Sign in"}</button>
      </form>
    </div>
  );
};

const toCsv = (rows) => {
  const cols = ["created_at", "name", "email", "phone", "route_name", "course_name", "consent_marketing", "answers.area", "answers.industry", "answers.goal", "answers.experience", "answers.blocker", "answers.hours", "answers.problem_idea", "answers.first_result"];
  const get = (r, c) => c.split(".").reduce((o, k) => o?.[k], r) ?? "";
  const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(get(r, c))).join(","))].join("\n");
};

export const downloadPdf = async (id) => {
  try {
    const res = await axios.get(`${API}/admin/leads/${id}/pdf`, { headers: authHeaders(), responseType: "blob" });
    window.open(URL.createObjectURL(res.data), "_blank");
  } catch (err) { toast.error(errorText(err)); }
};

const DiagnosticLeads = ({ onAuthFail }) => {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [route, setRoute] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: authHeaders() });
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(errorText(err));
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => leads.filter((l) =>
    (route === "all" || l.route === route) && `${l.name} ${l.email} ${l.phone || ""}`.toLowerCase().includes(q.toLowerCase())), [leads, q, route]);

  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([toCsv(shown)], { type: "text/csv" }));
    a.download = "artofresume-leads.csv";
    a.click();
  };

  const counts = ["foundation", "build", "architecture"].map((r) => leads.filter((l) => l.route === r).length);

  return (
    <div className="space-y-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[["Total leads", leads.length], ["Foundation", counts[0]], ["Build", counts[1]], ["Architecture", counts[2]]].map(([k, v]) => (
            <div key={k} data-testid={`admin-stat-${k.split(" ")[0].toLowerCase()}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-[13px] text-slate-600">{k}</p><p className="font-display text-[30px] font-bold text-[#0B1B33]">{v}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" />
            <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-9 pr-3 outline-none focus:border-[#0E9384]" />
          </div>
          <Select value={route} onValueChange={setRoute}>
            <SelectTrigger data-testid="admin-route-filter" className="h-12 bg-white md:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All routes</SelectItem><SelectItem value="foundation">Foundation</SelectItem>
              <SelectItem value="build">Build</SelectItem><SelectItem value="architecture">Architecture & Evidence</SelectItem>
            </SelectContent>
          </Select>
          <button data-testid="admin-export-btn" onClick={exportCsv} className="btn-teal flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-semibold"><Download className="h-4 w-4" /> Export CSV</button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table data-testid="admin-leads-table" className="w-full min-w-[900px] text-left text-[14px]">
            <thead className="bg-slate-50 text-[12px] uppercase tracking-wider text-slate-600">
              <tr>{["Date", "Lead", "Route / Course", "Hours/week", "Emails", "Status", "Notes", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((l) => (
                <tr key={l.id} data-testid="admin-lead-row" className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(l)}>
                  <td className="px-4 py-3 text-slate-600">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><p className="font-semibold text-[#0B1B33]">{l.name}</p><p className="text-slate-600">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p></td>
                  <td className="px-4 py-3"><span className="rounded-full bg-teal-50 px-2.5 py-1 text-[12px] font-semibold text-teal-800">{l.route_name}</span>{l.course_name && <p className="mt-1.5 text-[12px] text-slate-600">{l.course_name}</p>}</td>
                  <td className="px-4 py-3 text-slate-700">{l.answers?.hours}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">User: {l.user_email}<br />Admin: {l.admin_email}</td>
                  <CrmCells email={l.email} name={l.name} />
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={`admin-pdf-${l.id}`} onClick={() => downloadPdf(l.id)} className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-semibold text-[#0E9384] hover:bg-teal-50"><FileDown className="h-4 w-4" /> PDF</button>
                  </td>
                </tr>
              ))}
              {!shown.length && <tr><td colSpan={8} data-testid="admin-empty" className="px-4 py-12 text-center text-slate-600">No leads yet.</td></tr>}
            </tbody>
          </table>
        </div>
      <LeadDetail lead={open} onClose={() => setOpen(null)} />
    </div>
  );
};

const inviteToken = () => (window.location.hash.startsWith("#invite=") ? window.location.hash.slice(8) : null);

export default function Admin() {
  const [invite, setInvite] = useState(inviteToken);
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem("aor_admin_token")));
  const [me, setMe] = useState(null);
  const [tab, setTabState] = useState(() => window.location.hash.replace("#", "") || "home");
  const setTab = useCallback((k) => { setTabState(k); window.history.replaceState(null, "", `#${k}`); window.scrollTo(0, 0); }, []);
  const signOut = useCallback(() => { localStorage.removeItem("aor_admin_token"); setMe(null); setAuthed(false); }, []);

  useEffect(() => {
    if (!authed || invite) return;
    axios.get(`${API}/auth/me`, { headers: authHeaders() }).then((r) => setMe(r.data))
      .catch((e) => { if (e?.response?.status === 401) signOut(); else toast.error(errorText(e)); });
  }, [authed, invite, signOut]);

  const nav = useMemo(() => (me ? NAV.filter(([, area]) => canDo(me, area)) : []), [me]);

  if (invite) return <AcceptInvite token={invite} onDone={() => { setInvite(null); setTabState("home"); setAuthed(true); }} />;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;
  if (!me) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#0E9384]" /></div>;

  const items = nav.flatMap(([, , list]) => list);
  const current = items.some(([k]) => k === tab) ? tab : "home";
  const Pane = PANES[current];
  const label = items.find(([k]) => k === current)?.[1];
  const navBtn = ([k, l, Icon]) => (
    <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
      className={`flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[14px] font-semibold transition-colors ${current === k ? "bg-[#0B1B33] text-white" : "text-slate-700 hover:bg-slate-100"}`}>
      <Icon className="h-4 w-4 shrink-0" />{l}</button>
  );

  return (
    <MeContext.Provider value={me}>
    <AskProvider>
    <ContactsProvider>
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F7FAFC]">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between px-5 py-3 lg:px-8">
          <Logo sub="ADMIN" testId="admin-logo" />
          <div className="flex items-center gap-1">
            <span data-testid="admin-me" className="hidden px-3 text-right text-[13px] leading-tight text-slate-600 md:block"><b className="block text-[#0B1B33]">{me.name}</b>{me.role === "admin" ? "Owner" : "Coordinator"}</span>
            <a href="/login" target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-[14px] text-slate-700 hover:bg-slate-100 sm:flex"><ExternalLink className="h-4 w-4" /> Student portal</a>
            <button data-testid="admin-logout-btn" onClick={signOut} className="flex items-center gap-2 rounded-lg px-3 py-2 text-[14px] text-slate-700 hover:bg-slate-100"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
        <nav data-testid="admin-nav-mobile" className="flex gap-1 overflow-x-auto px-5 pb-3 lg:hidden">{items.map(navBtn)}</nav>
      </header>
      <div className="lg:flex">
        <aside data-testid="admin-nav" className="sticky top-[65px] hidden h-[calc(100vh-65px)] w-60 shrink-0 overflow-y-auto border-r border-slate-200 bg-white px-3 py-5 lg:block">
          {nav.map(([group, , items]) => (
            <div key={group || "top"} className="mb-4">
              {group && <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{group}</p>}
              <div className="flex flex-col gap-0.5">{items.map(navBtn)}</div>
            </div>
          ))}
        </aside>
        <main className="min-w-0 flex-1 space-y-5 px-5 py-6 lg:px-8">
          <h1 className="font-display text-[24px] font-bold text-[#0B1B33]">{current === "home" ? "Overview" : label}</h1>
          {current === "home" ? <AdminHome go={setTab} /> : current === "diagnostic" ? <DiagnosticLeads onAuthFail={signOut} /> : <Pane />}
        </main>
      </div>
    </div>
    </ContactsProvider>
    </AskProvider>
    </MeContext.Provider>
  );
}
